import React, { useContext, useEffect, useMemo, useState } from 'react';

import { DataLoading } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/DataLoading';
import { ColorGroupDebugger } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/DebugMenus/ColorGroupingDebugger';
import { FilterSelector } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector';
import { FilteredSearchResults } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults';
import { SearchByName } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/SearchByName';
import { VariationDetails } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/VariationDetails';
import { useTrackProductSelectionEvent } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useTrackProductSelectionEvent';
import { filterProductsFromDataStore } from 'src/modules/designStudio/helpers/productSelection/productFilterUtils';
import type {
  ProductFilter,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { ProductCategory } from 'src/modules/designStudio/helpers/productSelection/types';
import { productCatalogContext } from 'src/modules/designStudio/providers';
import { useQueryParam } from 'src/shared/hooks';

enum ActiveView {
  VIEW_FILTERED_PRODUCTS = 'VIEW_FILTERED_PRODUCTS',
  SELECT_FILTER_OPTIONS = 'SELECT_FILTER_OPTIONS',
  SEARCH_BY_NAME = 'SEARCH_BY_NAME',
  VIEW_PRODUCT_DETAILS = 'VIEW_PRODUCT_DETAILS',
}

export type ProductSearchParams = {
  onBack: () => void;
  title: string;
  category: ProductCategory;
  onVariationSelected: (variation: Variation) => void;
  initialFilter: ProductFilter;
};

export const ProductSearch = ({
  onBack,
  category,
  title,
  onVariationSelected,
  initialFilter,
}: ProductSearchParams) => {
  const debugColorMenu = useQueryParam('debug_color_groups');
  const showDebugColorMenu = typeof debugColorMenu !== 'undefined';

  const { pcDataStore } = useContext(productCatalogContext);
  const [activeView, setActiveView] = useState<ActiveView>(
    ActiveView.VIEW_FILTERED_PRODUCTS,
  );

  const [selectedVariation, setSelectedVariation] = useState<
    Variation | undefined
  >(undefined);

  const [filter, setFilter] = useState(initialFilter);

  const { trackRealProductSelectionEvent } = useTrackProductSelectionEvent({
    category,
  });

  /**
   * The filter screen can be opened by two buttons:
   * - color button: User wants to change only color
   * - filter button: User wants to change all filter options
   *
   * filterSelectorContext keeps track of which button was pressed to open filter screen.
   */
  const [filterSelectorContext, setFilterSelectorContext] = useState<
    'color' | 'all'
  >('all');

  const filteredProducts = useMemo(
    () => (pcDataStore ? filterProductsFromDataStore(filter, pcDataStore) : []),
    [filter, pcDataStore],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeView]);

  if (showDebugColorMenu) {
    return <ColorGroupDebugger />;
  }

  const actionsByView: Record<
    ActiveView,
    Record<string, (...args: any) => void>
  > = {
    VIEW_FILTERED_PRODUCTS: {
      onBack: () => onBack(),
      onSearchByName: () => {
        trackRealProductSelectionEvent({
          track_id: `product-selection-${category}-search-click`,
          button_text: 'search',
        });

        setActiveView(ActiveView.SEARCH_BY_NAME);
      },
      onApplyVariation: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${category}-tile-click`,
          },
          variation,
        );

        setSelectedVariation(variation);
        onVariationSelected(variation);
      },
      onViewVariationDetails: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${category}-tile-more-info-click`,
          },
          variation,
        );
        setSelectedVariation(variation);
        setActiveView(ActiveView.VIEW_PRODUCT_DETAILS);
      },
      onChangeFilterColor: () => {
        trackRealProductSelectionEvent({
          track_id: `product-selection-${category}-filter-click`,
          button_text: 'color',
        });
        setFilterSelectorContext('color');
        setActiveView(ActiveView.SELECT_FILTER_OPTIONS);
      },
      onChangeFilter: () => {
        trackRealProductSelectionEvent({
          track_id: `product-selection-${category}-filter-click`,
          button_text: 'more',
        });
        setFilterSelectorContext('all');
        setActiveView(ActiveView.SELECT_FILTER_OPTIONS);
      },
    },
    SELECT_FILTER_OPTIONS: {
      onUserChangedFilterSelection: (newFilter: ProductFilter) => {
        setFilter(newFilter);
      },
      onBack: () => {
        setActiveView(ActiveView.VIEW_FILTERED_PRODUCTS);
      },
      onView: () => {
        trackRealProductSelectionEvent({
          filter: JSON.stringify(filter, undefined, 2),
          track_id: `product-selection-${category}-filter-apply-click`,
          button_text: 'View',
        });
        setActiveView(ActiveView.VIEW_FILTERED_PRODUCTS);
      },
    },
    SEARCH_BY_NAME: {
      onCancel: () => {
        setActiveView(ActiveView.VIEW_FILTERED_PRODUCTS);
      },
      onViewProductDetails: () => {
        setActiveView(ActiveView.VIEW_PRODUCT_DETAILS);
      },
      onApplyVariation: (variation: Variation) => {
        setSelectedVariation(variation);
        onVariationSelected(variation);
      },
    },
    VIEW_PRODUCT_DETAILS: {
      onBack: () => {
        setActiveView(ActiveView.VIEW_FILTERED_PRODUCTS);
      },
      onDifferentSizeSelected: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${category}-size-select-click`,
          },
          variation,
        );

        setSelectedVariation(variation);
      },
      onDifferentColorSelected: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${category}-color-select-click`,
          },
          variation,
        );

        setSelectedVariation(variation);
      },
      onApply: () => {
        trackRealProductSelectionEvent(
          {
            filter: JSON.stringify(filter, undefined, 2),
            track_id: `product-selection-${category}-apply-click`,
            button_text: 'Apply',
          },
          selectedVariation,
        );
        onVariationSelected(selectedVariation!);
      },
    },
  };

  const viewByState: Record<ActiveView, React.ReactElement> = {
    [ActiveView.VIEW_FILTERED_PRODUCTS]: (() => {
      const actions = actionsByView[ActiveView.VIEW_FILTERED_PRODUCTS];
      return (
        <FilteredSearchResults
          category={category}
          filter={filter}
          filteredProducts={filteredProducts}
          onApplyVariation={actions.onApplyVariation}
          onBack={actions.onBack}
          onChangeFilter={actions.onChangeFilter}
          onChangeFilterColor={actions.onChangeFilterColor}
          onSearchByName={actions.onSearchByName}
          onViewVariationDetails={actions.onViewVariationDetails}
          selectedVariation={selectedVariation as Variation}
          title={title}
        />
      );
    })(),
    [ActiveView.SELECT_FILTER_OPTIONS]: (() => {
      const actions = actionsByView[ActiveView.SELECT_FILTER_OPTIONS];
      return (
        <FilterSelector
          category={category}
          filterSelectorContext={filterSelectorContext}
          onBack={actions.onBack}
          onUserChangedFilterSelection={actions.onUserChangedFilterSelection}
          onView={actions.onView}
          selectedFilter={filter}
          showProductTypes={category !== ProductCategory.TRIM}
        />
      );
    })(),
    [ActiveView.SEARCH_BY_NAME]: (() => {
      const actions = actionsByView[ActiveView.SEARCH_BY_NAME];
      return (
        <SearchByName
          category={category}
          onApplyVariation={actions.onApplyVariation}
          onCancel={actions.onCancel}
        />
      );
    })(),
    [ActiveView.VIEW_PRODUCT_DETAILS]: (() => {
      const actions = actionsByView[ActiveView.VIEW_PRODUCT_DETAILS];
      return (
        <VariationDetails
          category={category}
          onApply={actions.onApply}
          onBack={actions.onBack}
          onDifferentColorSelected={actions.onDifferentColorSelected}
          onDifferentSizeSelected={actions.onDifferentSizeSelected}
          variation={selectedVariation as Variation}
        />
      );
    })(),
  };

  if (!pcDataStore) {
    return <DataLoading />;
  }

  return viewByState[activeView];
};
